import { GetterTree } from 'vuex';
import RootState from '@/store/root_state';
import { UndergradDisciplineState } from './state';
import { UndergradDiscipline } from '@/models/undergrad/undergrad-discipline.model';
import { UndergradLesson } from '@/models/undergrad/undegrad-lesson.model';
import { UndergradLive } from '@/models/undergrad/undergrad-live.model';

const getters: GetterTree<UndergradDisciplineState, RootState> = {
  allDisciplineIds: (state: UndergradDisciplineState): string[] => {
    return state.allDisciplinesIds;
  },
  undergradDisciplines: (state: UndergradDisciplineState): UndergradDiscipline[] => {
    return Object.values(state.undergradDisciplines);
  },
  currentDisciplinesByCycle: (state: UndergradDisciplineState): UndergradDiscipline[] => {
    return state.currentDisciplinesByCycle;
  },
  onboardingDiscipline: (state: UndergradDisciplineState): UndergradDiscipline | undefined => {
    return Object.values(state.undergradDisciplines).find((d) =>
      d.name.toLowerCase().includes('onboard'),
    );
  },
  disciplineBySlug: (state: UndergradDisciplineState) => (disciplineSlug: string) => {
    return Object.values(state.undergradDisciplines).find(
      (discipline: UndergradDiscipline) => discipline.slug === disciplineSlug,
    );
  },
  disciplineById: (state: UndergradDisciplineState) => (disciplineId: string) => {
    return Object.values(state.undergradDisciplines).find(
      (discipline: UndergradDiscipline) => discipline.id === disciplineId,
    );
  },
  disciplineByInstanceId: (state: UndergradDisciplineState) => (instanceId: string) => {
    return Object.values(state.undergradDisciplines).find(
      (d) => d.disciplineInstanceId === instanceId,
    );
  },
  isLoadingDisciplineData: (state: UndergradDisciplineState) => (disciplineId: string) => {
    return state.isLoadingDisciplineData[disciplineId];
  },
  disciplineLessons:
    (state: UndergradDisciplineState) =>
    (disciplineId: string): UndergradLesson[] => {
      return Object.values(state.undergradDisciplines[disciplineId].lessons);
    },
  disciplineLessonById:
    (state: UndergradDisciplineState) =>
    (disciplineId: string, lessonId: string): UndergradLesson => {
      return state.undergradDisciplines[disciplineId].lessons[lessonId];
    },
  disciplineLives:
    (state: UndergradDisciplineState) =>
    (disciplineId: string): UndergradLive[] => {
      return Object.values(state.undergradDisciplines[disciplineId].lives).map(
        (live: UndergradLive) => ({
          ...live,
          transmissionStartDate: new Date(live.transmissionStartDate),
          transmissionEndDate: new Date(live.transmissionEndDate),
        }),
      );
    },
  disciplineLiveById:
    (state: UndergradDisciplineState) =>
    (disciplineId: string, liveId: string): UndergradLive => {
      return state.undergradDisciplines[disciplineId].lives[liveId];
    },
  homeUndergradDisciplinesLives: (state: UndergradDisciplineState): UndergradLive[] => {
    const todayDate = new Date();

    const homeLives: UndergradLive[] = [];

    const lives = Object.values(state.undergradDisciplines).flatMap((d: UndergradDiscipline) =>
      Object.values(d.lives),
    );

    Object.values(lives).forEach((live: UndergradLive) => {
      const startDate = new Date(live.transmissionStartDate);
      const endDate = new Date(live.transmissionEndDate);

      if (endDate >= todayDate) {
        homeLives.push({
          ...live,
          transmissionStartDate: startDate,
          transmissionEndDate: endDate,
        });
      }
    });

    return homeLives;
  },
  regularDisciplines: (state: UndergradDisciplineState): UndergradDiscipline[] => {
    return Object.values(state.undergradDisciplines).filter(
      (d) => d.submoduleType.toUpperCase() === 'REGULAR',
    );
  },
  complementaryDisciplines: (state: UndergradDisciplineState): UndergradDiscipline[] => {
    return Object.values(state.undergradDisciplines).filter(
      (d) => d.submoduleType.toUpperCase() === 'COMPLEMENTAR',
    );
  },
};

export default getters;
